import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ContactInfoSection } from '../components/booking/ContactInfoSection';
import { EventDetailsSection } from '../components/booking/EventDetailsSection';
import { PackageSelector } from '../components/booking/PackageSelector';
import { AddonsSelector } from '../components/booking/AddonsSelector';
import { MediaConsentModal } from '../components/booking/MediaConsentModal';
import { useBookingForm } from '../hooks/useBookingForm';

const BookingForm = () => {
  const {
    formData,
    loading,
    error,
    catalogLoading,
    packages,
    addons,
    consentModalOpen,
    handleInputChange,
    handlePackageSelect,
    handleAddonToggle,
    isAddonIncluded,
    submit,
    submitWithConsent,
  } = useBookingForm();

  if (catalogLoading) {
    return (
      <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center" data-testid="catalog-loading">
        <p className="text-[#8B7355] font-serif text-xl">Loading packages...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1E8] py-24" data-testid="booking-form-page">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-serif text-[#8B7355] mb-4 italic">Book Your Event</h1>
          <p className="text-lg text-[#8B7355]/80">
            Fill out the form below to enquire — we&apos;ll call you within 24 hours to confirm your date and arrange payment.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="booking-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-8">
          <ContactInfoSection formData={formData} onChange={handleInputChange} />
          <EventDetailsSection formData={formData} onChange={handleInputChange} />
          <PackageSelector packages={packages} selectedPackage={formData.package} onSelect={handlePackageSelect} />
          <AddonsSelector
            addons={addons}
            selectedAddons={formData.addons}
            isAddonIncluded={isAddonIncluded}
            onToggle={handleAddonToggle}
          />

          <Card className="border-2 border-[#8B7355]/20">
            <CardHeader>
              <CardTitle className="font-serif text-2xl text-[#8B7355]">Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                name="additional_notes"
                value={formData.additional_notes}
                onChange={handleInputChange}
                placeholder="Any special requests or questions?"
                rows={4}
                className="border-[#8B7355]/30"
                data-testid="booking-notes-input"
              />
            </CardContent>
          </Card>

          <div className="text-center">
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#8B7355] hover:bg-[#6B5845] text-white px-12 py-6 text-lg font-serif rounded-none"
              data-testid="booking-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Submit Enquiry'
              )}
            </Button>
            <p className="text-sm text-[#8B7355]/70 mt-4">
              We&apos;ll call you within 24 hours to confirm your date and arrange payment.
            </p>
          </div>
        </form>
      </div>

      <MediaConsentModal
        open={consentModalOpen}
        onAccept={submitWithConsent}
        onDecline={() => submitWithConsent(false)}
        loading={loading}
      />
    </div>
  );
};

export default BookingForm;
